/**
 * Comprehensive Umami Analytics Integration
 * Tracks all user interactions, revenue, and business events
 */

declare global {
  interface Window {
    umami?: {
      track: (
        event: string,
        properties?: Record<string, string | number | boolean>
      ) => Promise<void> | void
    }
  }
}

export interface UmamiEvent {
  name: string
  properties?: Record<string, string | number | boolean>
}

export interface RevenueEvent {
  amount: number
  currency: string
  orderId: string
  itemCount: number
  routeName?: string
  routeCode?: string
  tripInstanceId?: string
  customerEmail?: string
  paymentMethod?: string
}

export interface SearchEvent {
  searchType: 'route' | 'trip' | 'quick'
  fromLocation?: string
  toLocation?: string
  date?: string
  passengers?: number
  routeCode?: string
  resultsCount?: number
}

export interface AuthEvent {
  action: 'login_attempt' | 'login_success' | 'login_failure' | 'logout' | 'signup'
  method?: 'email' | 'google' | 'dev' | 'password'
  userRole?: string
  error?: string
}

export interface NavigationEvent {
  fromPage: string
  toPage: string
  navigationMethod: 'link' | 'button' | 'direct' | 'back' | 'forward'
}

export interface BookingEvent {
  action: 'booking_started' | 'booking_completed' | 'booking_abandoned' | 'payment_initiated' | 'payment_completed' | 'payment_failed'
  tripInstanceId?: string
  routeCode?: string
  routeName?: string
  passengers?: number
  amount?: number
  currency?: string
  orderId?: string
  step?: string
  error?: string
}

export interface TicketEvent {
  action: 'ticket_scanned' | 'ticket_validated' | 'ticket_invalid' | 'ticket_download'
  ticketCode?: string
  scanMethod?: 'qr' | 'manual'
  result?: 'valid' | 'invalid' | 'expired' | 'used'
  routeCode?: string
  driverEmail?: string
}

export interface AdminEvent {
  action: string
  section: 'routes' | 'trips' | 'tickets' | 'orders' | 'users' | 'charters' | 'dashboard'
  resourceId?: string
  resourceType?: string
}

class UmamiTracker {
  private isInitialized = false
  private eventQueue: UmamiEvent[] = []
  private isDevelopment = process.env.NODE_ENV === 'development'

  constructor() {
    if (typeof window !== 'undefined' && !this.isDevelopment) {
      this.initializeTracker()
    } else if (this.isDevelopment) {
      // In development, mark as initialized but don't actually track
      this.isInitialized = true
      console.log('[Umami] Development mode - analytics disabled')
    }
  }

  private initializeTracker() {
    // Simplified initialization for production only
    let attempts = 0
    const maxAttempts = 100 // 10 seconds max
    
    const checkUmami = () => {
      attempts++
      
      if (window.umami && typeof window.umami.track === 'function') {
        console.log('[Umami] Analytics initialized successfully')
        this.isInitialized = true
        this.flushEventQueue()
        return
      }
      
      if (attempts >= maxAttempts) {
        console.warn('[Umami] Analytics initialization timed out. Some events may not be tracked.')
        return
      }
      
      setTimeout(checkUmami, 100)
    }
    
    // Start checking after a brief delay to allow script to load
    setTimeout(checkUmami, 500)
  }

  private flushEventQueue() {
    while (this.eventQueue.length > 0) {
      const event = this.eventQueue.shift()
      if (event) {
        this.trackEvent(event.name, event.properties)
      }
    }
  }

  private trackEvent(name: string, properties?: Record<string, string | number | boolean>) {
    // Skip completely in development
    if (this.isDevelopment) {
      return
    }

    if (typeof window === 'undefined') {
      return
    }

    if (!this.isInitialized || !window.umami || typeof window.umami.track !== 'function') {
      // Queue the event for later
      this.eventQueue.push({ name, properties })
      return
    }

    try {
      window.umami.track(name, properties)
    } catch (error) {
      console.warn('[Umami] Failed to track event:', name, error)
    }
  }

  // Revenue Tracking
  trackRevenue(event: RevenueEvent) {
    this.trackEvent('revenue', {
      amount: event.amount,
      currency: event.currency,
      order_id: event.orderId,
      item_count: event.itemCount,
      route_name: event.routeName || '',
      route_code: event.routeCode || '',
      trip_instance_id: event.tripInstanceId || '',
      customer_email: event.customerEmail || '',
      payment_method: event.paymentMethod || '',
    })
  }

  // Search Tracking
  trackSearch(event: SearchEvent) {
    this.trackEvent('search', {
      search_type: event.searchType,
      from_location: event.fromLocation || '',
      to_location: event.toLocation || '',
      date: event.date || '',
      passengers: event.passengers || 1,
      route_code: event.routeCode || '',
      results_count: event.resultsCount || 0,
    })
  }

  // Authentication Tracking (safe for critical auth flows)
  trackAuth(event: AuthEvent) {
    // Extra safety wrapper - never let analytics interfere with auth
    try {
      this.trackEvent('auth', {
        action: event.action,
        method: event.method || '',
        user_role: event.userRole || '',
        error: event.error || '',
      })
    } catch (error) {
      // Completely silent failure for auth tracking
    }
  }

  // Navigation Tracking
  trackNavigation(event: NavigationEvent) {
    this.trackEvent('navigation', {
      from_page: event.fromPage,
      to_page: event.toPage,
      navigation_method: event.navigationMethod,
    })
  }

  // Booking Flow Tracking
  trackBooking(event: BookingEvent) {
    this.trackEvent('booking', {
      action: event.action,
      trip_instance_id: event.tripInstanceId || '',
      route_code: event.routeCode || '',
      route_name: event.routeName || '',
      passengers: event.passengers || 0,
      amount: event.amount || 0,
      currency: event.currency || '',
      order_id: event.orderId || '',
      step: event.step || '',
      error: event.error || '',
    })
  }

  // Ticket Scanning Tracking
  trackTicket(event: TicketEvent) {
    this.trackEvent('ticket', {
      action: event.action,
      ticket_code: event.ticketCode || '',
      scan_method: event.scanMethod || '',
      result: event.result || '',
      route_code: event.routeCode || '',
      driver_email: event.driverEmail || '',
    })
  }

  // Admin Actions Tracking
  trackAdmin(event: AdminEvent) {
    this.trackEvent('admin', {
      action: event.action,
      section: event.section,
      resource_id: event.resourceId || '',
      resource_type: event.resourceType || '',
    })
  }

  // Button/Link Clicks
  trackClick(element: string, location: string, properties?: Record<string, string | number | boolean>) {
    this.trackEvent('click', {
      element,
      location,
      ...properties,
    })
  }

  // Form Submissions
  trackFormSubmission(formName: string, success: boolean, properties?: Record<string, string | number | boolean>) {
    this.trackEvent('form_submission', {
      form_name: formName,
      success,
      ...properties,
    })
  }

  // Page Views (for SPAs)
  trackPageView(page: string, properties?: Record<string, string | number | boolean>) {
    this.trackEvent('page_view', {
      page,
      ...properties,
    })
  }

  // File Downloads
  trackDownload(fileName: string, fileType: string, location: string) {
    this.trackEvent('download', {
      file_name: fileName,
      file_type: fileType,
      location,
    })
  }

  // Custom Events
  trackCustom(eventName: string, properties?: Record<string, string | number | boolean>) {
    this.trackEvent(eventName, properties)
  }

  // Debug method for testing
  debug() {
    console.log('[Umami Debug]', {
      isInitialized: this.isInitialized,
      queueLength: this.eventQueue.length,
      umamiExists: typeof window !== 'undefined' ? !!window.umami : 'N/A (server)',
      umamiTrackExists: typeof window !== 'undefined' ? !!(window.umami && window.umami.track) : 'N/A (server)',
      queuedEvents: this.eventQueue
    })
    
    if (typeof window !== 'undefined' && window.umami) {
      console.log('[Umami Debug] Testing manual track call...')
      try {
        window.umami.track('debug-test', { timestamp: Date.now() })
        console.log('[Umami Debug] Manual track call succeeded')
      } catch (error) {
        console.error('[Umami Debug] Manual track call failed:', error)
      }
    }
  }
}

// Export singleton instance
export const umami = new UmamiTracker()

// Make available globally for debugging in browser console
if (typeof window !== 'undefined') {
  ;(window as any).umamiTracker = umami
}

// Export convenience functions
export const trackRevenue = (event: RevenueEvent) => umami.trackRevenue(event)
export const trackSearch = (event: SearchEvent) => umami.trackSearch(event)
export const trackAuth = (event: AuthEvent) => umami.trackAuth(event)
export const trackNavigation = (event: NavigationEvent) => umami.trackNavigation(event)
export const trackBooking = (event: BookingEvent) => umami.trackBooking(event)
export const trackTicket = (event: TicketEvent) => umami.trackTicket(event)
export const trackAdmin = (event: AdminEvent) => umami.trackAdmin(event)
export const trackClick = (element: string, location: string, properties?: Record<string, string | number | boolean>) => 
  umami.trackClick(element, location, properties)
export const trackFormSubmission = (formName: string, success: boolean, properties?: Record<string, string | number | boolean>) => 
  umami.trackFormSubmission(formName, success, properties)
export const trackPageView = (page: string, properties?: Record<string, string | number | boolean>) => 
  umami.trackPageView(page, properties)
export const trackDownload = (fileName: string, fileType: string, location: string) => 
  umami.trackDownload(fileName, fileType, location)
export const trackCustom = (eventName: string, properties?: Record<string, string | number | boolean>) => 
  umami.trackCustom(eventName, properties)
